import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TipTapEditor } from '../../components/admin/TipTapEditor';
import { AutocompleteSelector } from '../../components/admin/AutocompleteSelector';
import { API_BASE } from '../../config/api';

const HomepageSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const backendUrl = API_BASE;
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/homepage-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure all TipTap fields have proper default structure
        const normalizedSettings = {
          ...data.settings,
          hero: {
            ...data.settings.hero,
            headline: data.settings.hero.headline || {},
            description: data.settings.hero.description || {},
            slider_images: data.settings.hero.slider_images || []
          },
          partner_logos: {
            ...data.settings.partner_logos,
            logos: data.settings.partner_logos.logos || []
          },
          usp_grid: {
            ...data.settings.usp_grid,
            title: data.settings.usp_grid.title || {},
            description: data.settings.usp_grid.description || {},
            items: data.settings.usp_grid.items || []
          },
          ad_banners: {
            ...data.settings.ad_banners,
            items: data.settings.ad_banners.items || []
          },
          field_tags: {
            ...data.settings.field_tags,
            fields: data.settings.field_tags.fields || []
          },
          featured_universities: {
            ...data.settings.featured_universities,
            title: data.settings.featured_universities.title || {},
            description: data.settings.featured_universities.description || {},
            featured_ids: data.settings.featured_universities.featured_ids || []
          },
          featured_programs: {
            ...data.settings.featured_programs,
            title: data.settings.featured_programs.title || {},
            description: data.settings.featured_programs.description || {},
            featured_ids: data.settings.featured_programs.featured_ids || []
          },
          scholarship_discovery: {
            ...data.settings.scholarship_discovery,
            title: data.settings.scholarship_discovery.title || {},
            description: data.settings.scholarship_discovery.description || {},
            course_fields: data.settings.scholarship_discovery.course_fields || [],
            universities: data.settings.scholarship_discovery.universities || []
          },
          featured_blogs: {
            ...data.settings.featured_blogs,
            title: data.settings.featured_blogs.title || {},
            description: data.settings.featured_blogs.description || {}
          },
          final_cta: {
            ...data.settings.final_cta,
            headline: data.settings.final_cta.headline || {},
            description: data.settings.final_cta.description || {}
          }
        };
        setSettings(normalizedSettings);
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/homepage-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        alert('Homepage settings saved successfully!');
        await fetchSettings();
      } else {
        alert('Failed to save settings');
      }
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (section, data) => {
    setSettings({
      ...settings,
      [section]: { ...settings[section], ...data }
    });
  };

  const addArrayItem = (section, field, item) => {
    const current = settings[section][field] || [];
    updateSection(section, {
      [field]: [...current, item]
    });
  };

  const removeArrayItem = (section, field, index) => {
    const current = settings[section][field] || [];
    updateSection(section, {
      [field]: current.filter((_, i) => i !== index)
    });
  };

  const updateArrayItem = (section, field, index, data) => {
    const current = settings[section][field] || [];
    updateSection(section, {
      [field]: current.map((item, i) => i === index ? (typeof data === 'string' ? data : { ...item, ...data }) : item)
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E40AF] mx-auto mb-4"></div>
            <p className="text-[#6B7280]">Loading settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!settings) return null;

  const tabs = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'partner_logos', label: 'Partner Logos' },
    { id: 'usp_grid', label: 'USP Grid' },
    { id: 'ad_banners', label: 'Ad Banners' },
    { id: 'field_tags', label: 'Field Tags' },
    { id: 'featured_universities', label: 'Featured Universities' },
    { id: 'featured_programs', label: 'Featured Programs' },
    { id: 'scholarship_discovery', label: 'Scholarship Discovery' },
    { id: 'featured_blogs', label: 'Featured Blogs' },
    { id: 'final_cta', label: 'Final CTA' }
  ];

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[#111827]">Homepage Settings</h1>
            <p className="text-[#6B7280] mt-2">Manage all homepage sections and content</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-[#1E40AF] text-white font-semibold rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <div className="bg-white border-b border-[#E5E7EB] mb-6 overflow-x-auto">
          <div className="flex space-x-4 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#1E40AF] text-[#1E40AF]'
                    : 'border-transparent text-[#6B7280] hover:text-[#111827]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          
          {/* Hero Section */}
          {activeTab === 'hero' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.hero.enabled}
                    onChange={(e) => updateSection('hero', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Hero Section</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Headline (Rich Text)</label>
                <TipTapEditor
                  content={settings.hero.headline}
                  onChange={(data) => updateSection('hero', { headline: data })}
                  placeholder="Enter hero headline..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.hero.description}
                  onChange={(data) => updateSection('hero', { description: data })}
                  placeholder="Enter hero description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Primary Button Text</label>
                  <input
                    type="text"
                    value={settings.hero.primary_cta_text}
                    onChange={(e) => updateSection('hero', { primary_cta_text: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Primary Button URL</label>
                  <input
                    type="text"
                    value={settings.hero.primary_cta_url}
                    onChange={(e) => updateSection('hero', { primary_cta_url: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Secondary Button Text</label>
                  <input
                    type="text"
                    value={settings.hero.secondary_cta_text}
                    onChange={(e) => updateSection('hero', { secondary_cta_text: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Secondary Button URL</label>
                  <input
                    type="text"
                    value={settings.hero.secondary_cta_url}
                    onChange={(e) => updateSection('hero', { secondary_cta_url: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Slider Images</label>
                <p className="text-xs text-[#6B7280] mb-3">Recommended size: 1920×800px</p>
                {settings.hero.slider_images.map((img, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Image URL"
                      value={img.imageUrl}
                      onChange={(e) => updateArrayItem('hero', 'slider_images', idx, { ...img, imageUrl: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Alt text"
                      value={img.alt}
                      onChange={(e) => updateArrayItem('hero', 'slider_images', idx, { ...img, alt: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <button
                      onClick={() => removeArrayItem('hero', 'slider_images', idx)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('hero', 'slider_images', { imageUrl: '', alt: '' })}
                  className="mt-2 px-4 py-2 bg-[#E5E7EB] text-[#111827] rounded-lg hover:bg-[#D1D5DB]"
                >
                  + Add Image
                </button>
              </div>
            </div>
          )}

          {/* Partner Logos */}
          {activeTab === 'partner_logos' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.partner_logos.enabled}
                    onChange={(e) => updateSection('partner_logos', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Partner Logos</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Heading</label>
                <input
                  type="text"
                  value={settings.partner_logos.heading}
                  onChange={(e) => updateSection('partner_logos', { heading: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Logo Image URLs</label>
                <p className="text-xs text-[#6B7280] mb-3">Recommended size: 300×150px (transparent PNG)</p>
                {settings.partner_logos.logos.map((logo, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Logo URL"
                      value={logo}
                      onChange={(e) => updateArrayItem('partner_logos', 'logos', idx, e.target.value)}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <button
                      onClick={() => removeArrayItem('partner_logos', 'logos', idx)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('partner_logos', 'logos', '')}
                  className="mt-2 px-4 py-2 bg-[#E5E7EB] text-[#111827] rounded-lg hover:bg-[#D1D5DB]"
                >
                  + Add Logo
                </button>
              </div>
            </div>
          )}

          {/* USP Grid */}
          {activeTab === 'usp_grid' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.usp_grid.enabled}
                    onChange={(e) => updateSection('usp_grid', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable USP Grid</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Title (Rich Text)</label>
                <TipTapEditor
                  content={settings.usp_grid.title}
                  onChange={(data) => updateSection('usp_grid', { title: data })}
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.usp_grid.description}
                  onChange={(data) => updateSection('usp_grid', { description: data })}
                  placeholder="Enter description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">USP Items (6 images)</label>
                <p className="text-xs text-[#6B7280] mb-3">Recommended size: 600×400px</p>
                {settings.usp_grid.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Image URL"
                      value={item.imageUrl}
                      onChange={(e) => updateArrayItem('usp_grid', 'items', idx, { ...item, imageUrl: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Link URL"
                      value={item.linkUrl}
                      onChange={(e) => updateArrayItem('usp_grid', 'items', idx, { ...item, linkUrl: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Alt text"
                      value={item.alt}
                      onChange={(e) => updateArrayItem('usp_grid', 'items', idx, { ...item, alt: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <button
                      onClick={() => removeArrayItem('usp_grid', 'items', idx)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('usp_grid', 'items', { imageUrl: '', linkUrl: '#', alt: '' })}
                  className="mt-2 px-4 py-2 bg-[#E5E7EB] text-[#111827] rounded-lg hover:bg-[#D1D5DB]"
                >
                  + Add Item
                </button>
              </div>
            </div>
          )}

          {/* Ad Banners */}
          {activeTab === 'ad_banners' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.ad_banners.enabled}
                    onChange={(e) => updateSection('ad_banners', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Ad Banners</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Banner Items</label>
                <p className="text-xs text-[#6B7280] mb-3">Recommended size: 1200×300px</p>
                {settings.ad_banners.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Image URL"
                      value={item.imageUrl}
                      onChange={(e) => updateArrayItem('ad_banners', 'items', idx, { ...item, imageUrl: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Click URL"
                      value={item.clickUrl}
                      onChange={(e) => updateArrayItem('ad_banners', 'items', idx, { ...item, clickUrl: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Alt text"
                      value={item.alt}
                      onChange={(e) => updateArrayItem('ad_banners', 'items', idx, { ...item, alt: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <button
                      onClick={() => removeArrayItem('ad_banners', 'items', idx)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('ad_banners', 'items', { imageUrl: '', clickUrl: '#', alt: '' })}
                  className="mt-2 px-4 py-2 bg-[#E5E7EB] text-[#111827] rounded-lg hover:bg-[#D1D5DB]"
                >
                  + Add Banner
                </button>
              </div>
            </div>
          )}

          {/* Field Tags */}
          {activeTab === 'field_tags' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.field_tags.enabled}
                    onChange={(e) => updateSection('field_tags', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Field Tags</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Title</label>
                <input
                  type="text"
                  value={settings.field_tags.title}
                  onChange={(e) => updateSection('field_tags', { title: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description</label>
                <textarea
                  value={settings.field_tags.description}
                  onChange={(e) => updateSection('field_tags', { description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Fields</label>
                {settings.field_tags.fields.map((field, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Field name"
                      value={field.name}
                      onChange={(e) => updateArrayItem('field_tags', 'fields', idx, { ...field, name: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Link URL"
                      value={field.href}
                      onChange={(e) => updateArrayItem('field_tags', 'fields', idx, { ...field, href: e.target.value })}
                      className="flex-1 px-4 py-2 border border-[#D1D5DB] rounded-lg"
                    />
                    <button
                      onClick={() => removeArrayItem('field_tags', 'fields', idx)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('field_tags', 'fields', { name: '', href: '#' })}
                  className="mt-2 px-4 py-2 bg-[#E5E7EB] text-[#111827] rounded-lg hover:bg-[#D1D5DB]"
                >
                  + Add Field
                </button>
              </div>
            </div>
          )}

          {/* Featured Universities */}
          {activeTab === 'featured_universities' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.featured_universities.enabled}
                    onChange={(e) => updateSection('featured_universities', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Featured Universities</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Title (Rich Text)</label>
                <TipTapEditor
                  content={settings.featured_universities.title}
                  onChange={(data) => updateSection('featured_universities', { title: data })}
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.featured_universities.description}
                  onChange={(data) => updateSection('featured_universities', { description: data })}
                  placeholder="Enter description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Max Items to Show</label>
                <input
                  type="number"
                  value={settings.featured_universities.max_items}
                  onChange={(e) => updateSection('featured_universities', { max_items: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                />
              </div>

              <AutocompleteSelector
                label="Featured Universities"
                placeholder="Search universities by name..."
                selectedIds={settings.featured_universities.featured_ids || []}
                onChange={(ids) => updateSection('featured_universities', { featured_ids: ids })}
                fetchItems={async () => {
                  const token = localStorage.getItem('admin_token');
                  const response = await fetch(`${backendUrl}/api/admin/universities`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (response.ok) {
                    const data = await response.json();
                    return data.universities.map(uni => ({
                      id: uni.id,
                      name: uni.name,
                      description: `${uni.city}, ${uni.country}`
                    }));
                  }
                  return [];
                }}
                helperText="Search and select universities to feature. Leave empty to auto-select featured universities."
              />
            </div>
          )}

          {/* Featured Programs */}
          {activeTab === 'featured_programs' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.featured_programs.enabled}
                    onChange={(e) => updateSection('featured_programs', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Featured Programs</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Title (Rich Text)</label>
                <TipTapEditor
                  content={settings.featured_programs.title}
                  onChange={(data) => updateSection('featured_programs', { title: data })}
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.featured_programs.description}
                  onChange={(data) => updateSection('featured_programs', { description: data })}
                  placeholder="Enter description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Max Items to Show</label>
                <input
                  type="number"
                  value={settings.featured_programs.max_items}
                  onChange={(e) => updateSection('featured_programs', { max_items: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                />
              </div>

              <AutocompleteSelector
                label="Featured Programs"
                placeholder="Search programs by name..."
                selectedIds={settings.featured_programs.featured_ids || []}
                onChange={(ids) => updateSection('featured_programs', { featured_ids: ids })}
                fetchItems={async () => {
                  const token = localStorage.getItem('admin_token');
                  const response = await fetch(`${backendUrl}/api/admin/programs`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (response.ok) {
                    const data = await response.json();
                    return data.programs.map(prog => ({
                      id: prog.id,
                      name: prog.program_name,
                      description: `${prog.level_of_study} - ${prog.field_of_study_primary || 'General'}`
                    }));
                  }
                  return [];
                }}
                helperText="Search and select programs to feature. Leave empty to auto-select featured programs."
              />
            </div>
          )}

          {/* Scholarship Discovery */}
          {activeTab === 'scholarship_discovery' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.scholarship_discovery.enabled}
                    onChange={(e) => updateSection('scholarship_discovery', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Scholarship Discovery</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Title (Rich Text)</label>
                <TipTapEditor
                  content={settings.scholarship_discovery.title}
                  onChange={(data) => updateSection('scholarship_discovery', { title: data })}
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.scholarship_discovery.description}
                  onChange={(data) => updateSection('scholarship_discovery', { description: data })}
                  placeholder="Enter description..."
                />
              </div>
            </div>
          )}

          {/* Featured Blogs */}
          {activeTab === 'featured_blogs' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.featured_blogs.enabled}
                    onChange={(e) => updateSection('featured_blogs', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Featured Blogs</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Title (Rich Text)</label>
                <TipTapEditor
                  content={settings.featured_blogs.title}
                  onChange={(data) => updateSection('featured_blogs', { title: data })}
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.featured_blogs.description}
                  onChange={(data) => updateSection('featured_blogs', { description: data })}
                  placeholder="Enter description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Max Items to Show</label>
                <input
                  type="number"
                  value={settings.featured_blogs.max_items}
                  onChange={(e) => updateSection('featured_blogs', { max_items: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                />
              </div>
              <p className="text-sm text-[#6B7280]">Featured blogs are managed from the Blog Posts page</p>
            </div>
          )}

          {/* Final CTA */}
          {activeTab === 'final_cta' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.final_cta.enabled}
                    onChange={(e) => updateSection('final_cta', { enabled: e.target.checked })}
                    className="w-5 h-5 text-[#1E40AF] rounded"
                  />
                  <span className="ml-2 font-medium text-[#111827]">Enable Final CTA</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Headline (Rich Text)</label>
                <TipTapEditor
                  content={settings.final_cta.headline}
                  onChange={(data) => updateSection('final_cta', { headline: data })}
                  placeholder="Enter headline..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Description (Rich Text)</label>
                <TipTapEditor
                  content={settings.final_cta.description}
                  onChange={(data) => updateSection('final_cta', { description: data })}
                  placeholder="Enter description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Primary Button Text</label>
                  <input
                    type="text"
                    value={settings.final_cta.primary_button_text}
                    onChange={(e) => updateSection('final_cta', { primary_button_text: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Primary Button URL</label>
                  <input
                    type="text"
                    value={settings.final_cta.primary_button_url}
                    onChange={(e) => updateSection('final_cta', { primary_button_url: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Secondary Button Text</label>
                  <input
                    type="text"
                    value={settings.final_cta.secondary_button_text}
                    onChange={(e) => updateSection('final_cta', { secondary_button_text: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Secondary Button URL</label>
                  <input
                    type="text"
                    value={settings.final_cta.secondary_button_url}
                    onChange={(e) => updateSection('final_cta', { secondary_button_url: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Background Image URL (Optional)</label>
                <p className="text-xs text-[#6B7280] mb-2">Recommended size: 1600×600px. Leave empty for default gradient background.</p>
                <input
                  type="text"
                  value={settings.final_cta.image_url}
                  onChange={(e) => updateSection('final_cta', { image_url: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg"
                  placeholder="Optional background image"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-[#1E40AF] text-white font-semibold rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default HomepageSettings;
